import { useQuery } from '@tanstack/react-query'
import React, { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
// import { useAuth } from '../auth/AuthContext'
import { Permissions } from '../auth/rbac'
import { useAccess } from '../auth/useAccess'
import { useDebouncedValue } from '../hooks/useDebouncedValue'
import { api } from '../lib/api'
import { asCollection } from '../lib/collections'
import { formatDateTimeMinute } from '../lib/date'
import { NoticeStatusLabel } from '../lib/labels'
import ListContainer from '../ui/ListContainer'
import MenuGuideButton from '../components/MenuGuideButton'

export default function NoticesPage() {
    const { user, guard, allow } = useAccess()
    const navigate = useNavigate()
    const [q, setQ] = useState('')
    const [status, setStatus] = useState('ALL')
    const [searchParams, setSearchParams] = useSearchParams()
    const [noticeOrgType, setNoticeOrgType] = useState(searchParams.get('noticeOrgType') || '')
    const [page, setPage] = useState(Number(searchParams.get('page') || '1'))
    const [size, setSize] = useState(Number(searchParams.get('size') || '10'))
    const [sort, setSort] = useState(searchParams.get('sort') || 'createdAt,DESC')
    const [items, setItems] = useState([])
    // totalElements는 백엔드에서 제공하지만, 현재는 목록 "번호(seq)"를 서버가 내려주므로 화면에서 사용하지 않는다.
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    // 조직 범위: 내 조직 + 하위 조직 (단일 선택)
    const [orgScopeId, setOrgScopeId] = useState(searchParams.get('targetOrgIds') || '')
    const [mine, setMine] = useState(searchParams.get('mine') === '1' || searchParams.get('mine') === 'true')
    const [orgScopeOptions, setOrgScopeOptions] = useState([])
    const dq = useDebouncedValue(q, 300)
    const [bulkReadLoading, setBulkReadLoading] = useState(false)

    // 상태 필터는 공지 작성 권한이 있는 사용자만 노출/사용
    const canFilterStatus = allow.perm([Permissions.NOTICE_CREATE])

    // URL 동기화
    useEffect(() => {
        const next = new URLSearchParams()
        if (dq) next.set('q', dq)
        if (status && status !== 'ALL') next.set('status', status)
        if (noticeOrgType) next.set('noticeOrgType', noticeOrgType)
        if (page && page !== 1) next.set('page', String(page))
        if (size && size !== 10) next.set('size', String(size))
        if (sort && sort !== 'createdAt,DESC') next.set('sort', sort)
        if (orgScopeId) next.set('targetOrgIds', orgScopeId)
        if (mine) next.set('mine', '1')
        setSearchParams(next, { replace: true })
    }, [dq, status, noticeOrgType, page, size, sort, orgScopeId, mine, setSearchParams])

    const listQuery = useQuery({
        queryKey: ['notices', { dq, status, noticeOrgType, page, size, sort, orgScopeId, mine }],
        queryFn: async () => {
            const params = {}
            if (status !== 'ALL') params.status = status
            if (dq) params.q = dq
            if (orgScopeId) params.targetOrgIds = orgScopeId
            if (noticeOrgType) params.noticeOrgType = noticeOrgType
            if (mine) params.mine = true
            params.page = page - 1
            params.size = size
            params.sort = sort
            const res = await api.get('/notices', { params })
            const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
            const raw = asCollection(payload)
            const totalElements = Number(payload?.totalElements ?? payload?.total ?? raw.length ?? 0)
            // 백엔드 NoticeListDto를 테이블에서 기대하는 키로 매핑
            const mapped = raw.map((n) => ({
                seq: n.seq ?? null,
                id: n.id,
                isRead: (n.isRead === undefined || n.isRead === null) ? true : !!n.isRead,
                orgId: n.orgId,
                insurerName: n.insurerName || null,
                title: n.title,
                status: n.status,
                target: n.targetSummary || n.orgName || '-',
                targetCount: n.targetCount || 0,
                createdBy: n.creatorName || n.createdByName || '-',
                createdAt: n.createdAt || '-',
                updatedAt: n.updatedAt || n.approvedAt || n.sentAt || n.createdAt || '-',
            }))
            return { items: mapped, total: totalElements }
        },
        keepPreviousData: true,
    })
    useEffect(() => {
        const data = listQuery.data?.items || []
        const norm = (sort || '').toLowerCase()
        const shouldDescByCreatedAt = norm === '' || (norm.startsWith('createdat') && !norm.endsWith(',asc'))
        const arranged = shouldDescByCreatedAt
            ? [...data].sort((a, b) => {
                const aTime = a?.createdAt ? new Date(a.createdAt).getTime() : 0
                const bTime = b?.createdAt ? new Date(b.createdAt).getTime() : 0
                return bTime - aTime
            })
            : data
        setItems(arranged)
        setLoading(listQuery.isFetching)
        setError(listQuery.isError ? '목록을 불러오지 못했습니다' : null)
    }, [listQuery.data, listQuery.isFetching, listQuery.isError, sort])

    const filtered = useMemo(() => items, [items])
    const unreadApprovedCount = useMemo(
        () => filtered.filter(x => x.status === 'APPROVED' && !x.isRead).length,
        [filtered]
    )
    const roles = Array.isArray(user?.roles) ? user.roles : []
    const isGaUser = roles.some(r => typeof r === 'string' && r.startsWith('GA_'))
    const showInsurerColumn = isGaUser

    const handleRowNavigate = (e, noticeId) => {
        // 텍스트 드래그/복사 중 클릭은 이동하지 않도록(UX)
        try {
            const sel = window.getSelection && window.getSelection()
            const selectedText = sel ? String(sel) : ''
            if (selectedText && selectedText.trim().length > 0) return
        } catch { }

        // 내부 컨트롤(버튼/인풋 등) 클릭은 row 이동을 막는다.
        try {
            const el = e?.target
            if (el && el.closest) {
                const hit = el.closest('button, a, input, select, textarea, label')
                if (hit) return
            }
        } catch { }

        navigate(`/notices/${noticeId}`)
    }

    // 조직 범위 옵션 로드
    // - 기본: 내 조직 + 하위 조직 (`/orgs/options?scope=mine`)
    // - GA 관리자/지점장 계열: 여기에 "우리 지점에 매핑된(공지 대상으로 설정 가능한) GA 지점"도 추가
    useEffect(() => {
        let ignore = false
        async function loadOrgScope() {
            try {
                const res = await api.get('/orgs/options', { params: { scope: 'mine' } })
                const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
                let base = Array.isArray(payload) ? payload : []

                // GA 관리 계열(대표/관리자)인 경우,
                // 우리 지점에 매핑된 원수사 지점들을 통해 공지 대상으로 설정 가능한 GA 지점들도 함께 노출
                const roles = Array.isArray(user?.roles) ? user.roles : []
                const gaManagerRoles = [
                    'GA_LEADER',
                    'GA_MANAGER',
                ]
                const isGaNoticeManager = roles.some(r => gaManagerRoles.includes(r))

                if (isGaNoticeManager && user?.orgId) {
                    try {
                        // 1) 내 조직 상세에서 매핑된 원수사(branch) 목록 조회
                        const detailRes = await api.get(`/orgs/${user.orgId}`)
                        const detail = (detailRes?.data && detailRes.data.data !== undefined) ? detailRes.data.data : detailRes.data
                        const mappedInsurers = Array.isArray(detail?.mappedInsurers) ? detail.mappedInsurers : []

                        // 2) 각 원수사 지점이 매핑하고 있는 GA 지점 목록을 모아서 옵션에 병합
                        const byId = new Map()
                        base.forEach(o => {
                            if (o && o.id != null) byId.set(String(o.id), o)
                        })

                        for (const ins of mappedInsurers) {
                            if (!ins?.id) continue
                            try {
                                const insRes = await api.get(`/orgs/${ins.id}`)
                                const insDetail = (insRes?.data && insRes.data.data !== undefined) ? insRes.data.data : insRes.data
                                const mappedGAs = Array.isArray(insDetail?.mappedGAs) ? insDetail.mappedGAs : []
                                for (const ga of mappedGAs) {
                                    if (!ga?.id) continue
                                    const key = String(ga.id)
                                    if (!byId.has(key)) {
                                        byId.set(key, {
                                            id: ga.id,
                                            name: ga.name,
                                            type: 'GA',
                                            parentOrgId: null,
                                        })
                                    }
                                }
                            } catch {
                                // 개별 원수사 지점 조회 실패는 무시
                            }
                        }

                        base = Array.from(byId.values())
                    } catch {
                        // 매핑 정보 조회 실패 시 기본 옵션만 사용
                    }
                }

                if (!ignore) setOrgScopeOptions(base)
            } catch {
                if (!ignore) setOrgScopeOptions([])
            }
        }
        loadOrgScope()
        return () => { ignore = true }
    }, [user])

    // 정렬 핸들러/아이콘
    const handleSort = (column) => {
        const [currentColumn, currentDirection] = sort.split(',')
        let newDirection = 'ASC'
        if (currentColumn === column) newDirection = currentDirection === 'ASC' ? 'DESC' : 'ASC'
        setSort(`${column},${newDirection}`)
        setPage(1)
    }
    const getSortIcon = (column) => {
        const [currentColumn, currentDirection] = sort.split(',')
        if (currentColumn !== column) return '↕'
        return currentDirection === 'ASC' ? '↑' : '↓'
    }

    return (
        <section>
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <h1 className="text-2xl font-bold">공지 관리</h1>
                    <MenuGuideButton slug="notices" title="공지관리 가이드" />
                </div>
                <Link {...guard.hideIfNoPerm([Permissions.NOTICE_CREATE])} to="/notices/new" className="rounded bg-teal-700 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal-800">+ 새 공지</Link>
            </div>
            <div className="mt-4 flex items-center gap-2 flex-wrap">
                <input className="w-full md:w-80 rounded border px-3 py-2 text-sm" placeholder="검색: 제목" value={q} onChange={(e) => setQ(e.target.value)} />
                <select
                    className="rounded border px-3 py-2 text-sm"
                    value={noticeOrgType}
                    onChange={(e) => {
                        setNoticeOrgType(e.target.value || '')
                        setPage(1)
                    }}
                    title="공지 출처(작성 조직 타입)"
                >
                    <option value="">전체(출처)</option>
                    <option value="GA">GA 공지</option>
                    <option value="INSURER">원수사 공지</option>
                </select>
                {canFilterStatus && (
                    <select className="rounded border px-3 py-2 text-sm" value={status} onChange={(e) => setStatus(e.target.value)}>
                        <option value="ALL">전체</option>
                        <option value="DRAFT">작성중</option>
                        <option value="APPROVED">승인됨</option>
                    </select>
                )}
                <label className="flex items-center gap-2 text-sm select-none">
                    <input
                        type="checkbox"
                        className="rounded border-slate-300"
                        checked={mine}
                        onChange={(e) => {
                            setMine(e.target.checked)
                            setPage(1)
                        }}
                    />
                    내가 작성한 공지
                </label>
                <select className="rounded border px-3 py-2 text-sm" value={size} onChange={(e) => { setSize(Number(e.target.value)); setPage(1) }}>
                    <option value={10}>10개</option>
                    <option value={20}>20개</option>
                    <option value={50}>50개</option>
                </select>
                {/* 조직 범위 선택: 내 조직 + 하위 조직 (단일 선택) */}
                <select
                    className="rounded border px-3 py-2 text-sm"
                    value={orgScopeId}
                    onChange={(e) => { setOrgScopeId(e.target.value); setPage(1) }}
                >
                    <option value="">전체보기</option>
                    {orgScopeOptions.map(o => (
                        <option key={o.id} value={o.id}>{o.name}</option>
                    ))}
                </select>
                <button
                    type="button"
                    className="rounded border px-3 py-2 text-sm disabled:opacity-50"
                    disabled={bulkReadLoading || unreadApprovedCount <= 0}
                    onClick={async () => {
                        const ok = window.confirm(`미읽음 공지 ${unreadApprovedCount}건을 모두 읽음 처리할까요?`)
                        if (!ok) return
                        setBulkReadLoading(true)
                        try {
                            const params = {}
                            if (noticeOrgType) params.noticeOrgType = noticeOrgType
                            if (orgScopeId) params.targetOrgIds = orgScopeId
                            if (dq) params.q = dq
                            if (mine) params.mine = true
                            const res = await api.post('/notices/mark-all-read', { deviceType: 'WEB', location: 'NOTICE_LIST' }, { params })
                            const marked = res?.data?.data?.marked ?? res?.data?.marked ?? 0
                            await listQuery.refetch()
                            window.alert(`읽음 처리 완료: ${marked}건`)
                        } catch {
                            window.alert('읽음 처리에 실패했습니다.')
                        } finally {
                            setBulkReadLoading(false)
                        }
                    }}
                    title="현재 목록 조건(출처/대상/검색/내공지) 기준으로 승인된 미읽음 공지를 모두 읽음 처리합니다."
                >
                    {bulkReadLoading ? '처리중…' : `미읽음 전체 읽음(${unreadApprovedCount})`}
                </button>
                {/* <button className="rounded border px-3 py-2 text-sm" onClick={async () => {
                    try {
                        const params = { q: dq || undefined, status: status !== 'ALL' ? status : undefined }
                        if (orgScopeId) params.targetOrgIds = orgScopeId
                        const res = await api.get('/notices/export', { params, responseType: 'blob' })
                        const url = URL.createObjectURL(res.data)
                        const a = document.createElement('a')
                        a.href = url
                        a.download = 'notices.csv'
                        a.click()
                        URL.revokeObjectURL(url)
                    } catch { }
                }}>CSV 내보내기(서버)</button> */}
            </div>
            <div className="mt-4">
                <ListContainer
                    loading={loading}
                    error={error}
                    onRetry={() => listQuery.refetch()}
                    header={null}
                    table={(
                        <table className="min-w-full text-sm">
                            <thead className="bg-slate-50 text-slate-600">
                                <tr>
                                    <th className="px-4 py-2 text-left">번호</th>
                                    {showInsurerColumn && (
                                        <th className="px-4 py-2 text-left">보험사</th>
                                    )}
                                    <th className="px-4 py-2 text-left cursor-pointer select-none hover:bg-slate-100" onClick={() => handleSort('title')}>제목 {getSortIcon('title')}</th>
                                    <th className="px-4 py-2 text-left">작성자</th>
                                    <th className="px-4 py-2 text-left">대상</th>
                                    <th className="px-4 py-2 text-left cursor-pointer select-none hover:bg-slate-100" onClick={() => handleSort('createdAt')}>생성일 {getSortIcon('createdAt')}</th>
                                    <th className="px-4 py-2 text-left cursor-pointer select-none hover:bg-slate-100" onClick={() => handleSort('updatedAt')}>최종수정일 {getSortIcon('updatedAt')}</th>
                                    <th className="px-4 py-2 text-left cursor-pointer select-none hover:bg-slate-100" onClick={() => handleSort('status')}>상태 {getSortIcon('status')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((n, idx) => {
                                    const seq = (n.seq != null) ? n.seq : (((page - 1) * size) + (idx + 1))
                                    const isUnreadApproved = n.status === 'APPROVED' && !n.isRead
                                    return (
                                        <tr
                                            key={n.id}
                                            role="button"
                                            tabIndex={0}
                                            onClick={(e) => handleRowNavigate(e, n.id)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') handleRowNavigate(e, n.id)
                                            }}
                                            className={`border-t cursor-pointer hover:bg-slate-50 ${isUnreadApproved ? 'bg-blue-50/40' : ''}`}
                                        >
                                            <td className="px-4 py-2">{seq}</td>
                                            {showInsurerColumn && (
                                                <td className="px-4 py-2">{n.insurerName || '-'}</td>
                                            )}
                                            <td className="px-4 py-2 text-brand-navy">
                                                <span className={isUnreadApproved ? 'font-semibold' : ''}>{n.title}</span>
                                                {isUnreadApproved && (
                                                    <span className="ml-2 inline-flex items-center rounded-full bg-blue-600 px-2 py-0.5 text-[10px] font-semibold text-white">
                                                        미읽음
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-4 py-2">{n.createdBy}</td>
                                            <td className="px-4 py-2">{n.target || '-'}</td>
                                            <td className="px-4 py-2">{formatDateTimeMinute(n.createdAt)}</td>
                                            <td className="px-4 py-2">{formatDateTimeMinute(n.updatedAt)}</td>
                                            <td className="px-4 py-2">{NoticeStatusLabel[n.status] || n.status}</td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    )}
                    footer={(
                        <div className="flex items-center justify-between p-3 border-t text-sm">
                            <button className="rounded border px-3 py-1 disabled:opacity-50" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>이전</button>
                            <div className="flex items-center gap-2">
                                <span>페이지 {page}</span>
                            </div>
                            <button className="rounded border px-3 py-1" onClick={() => setPage(p => p + 1)}>다음</button>
                        </div>
                    )}
                />
            </div>
        </section>
    )
}


